import { WindowState } from '../state';
import { SectionType, SectionState, SectionInterface } from '../types';

function easeInOutCubic(t: number): number {
  return t < 0.5
    ? 4 * t * t * t
    : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function getMaxAngle(type: SectionType): number {
  switch (type) {
    case SectionType.CAS_IN:
    case SectionType.CAS_OUT:
      return Math.PI / 2;
    case SectionType.TT:
      return Math.PI / 2;
    case SectionType.TILT_IN:
      return Math.PI / 12;
    case SectionType.TOPHUNG_OUT:
      return Math.PI / 6;
    case SectionType.STOLP:
      return Math.PI / 2;
    case SectionType.PIVOT_V:
      return Math.PI / 2;
    case SectionType.SLIDE_H:
    case SectionType.SLIDE_V:
      return Math.PI / 2;
    default:
      return 0;
  }
}

function getTiltAngle(): number {
  return Math.PI / 12; // ~15 degrees
}

function isOperableType(type: SectionType): boolean {
  return type !== SectionType.FIX;
}

interface ActiveAnimation {
  from: number;
  to: number;
  elapsed: number;
  duration: number;
  sectionIndex: number;
  mode: 'open' | 'tilt';
}

export class AnimationController {
  private animations: ActiveAnimation[] = [];

  constructor(private state: WindowState) {}

  toggleSection(sectionIndex: number, sectionInterfaces: Map<number, SectionInterface>): void {
    const config = this.state.config.sections[sectionIndex];
    if (!config || !isOperableType(config.type)) return;

    const current = config.openAmount;
    const isClosed = current < 0.01;

    // For TT type: alternate between tilt and turn
    if (config.type === SectionType.TT) {
      if (isClosed) {
        // First open = tilt
        this.startAnimation(sectionIndex, 0, 1, 'tilt');
      } else if (config.openState === SectionState.TILT) {
        // Close from tilt
        this.startAnimation(sectionIndex, 1, 0, 'tilt');
      } else {
        // Close from turn
        this.startAnimation(sectionIndex, 1, 0, 'open');
      }
      return;
    }

    // For all other types: simple toggle
    if (isClosed) {
      this.startAnimation(sectionIndex, 0, 1, 'open');
    } else {
      this.startAnimation(sectionIndex, 1, 0, 'open');
    }
  }

  /** Double-click for TT: switch to turn mode */
  turnSection(sectionIndex: number): void {
    const config = this.state.config.sections[sectionIndex];
    if (!config || config.type !== SectionType.TT) return;

    if (config.openState === SectionState.TILT && config.openAmount > 0.5) {
      // Close tilt first, then open turn
      this.startAnimation(sectionIndex, config.openAmount, 0, 'tilt');
      setTimeout(() => {
        this.startAnimation(sectionIndex, 0, 1, 'open');
      }, 450);
    } else if (config.openAmount < 0.01) {
      this.startAnimation(sectionIndex, 0, 1, 'open');
    } else {
      this.startAnimation(sectionIndex, 1, 0, 'open');
    }
  }

  private startAnimation(
    sectionIndex: number,
    from: number,
    to: number,
    mode: 'open' | 'tilt',
  ): void {
    // Cancel any existing animation on this section
    this.animations = this.animations.filter(a => a.sectionIndex !== sectionIndex);

    this.animations.push({
      from,
      to,
      elapsed: 0,
      duration: 0.4,
      sectionIndex,
      mode,
    });

    // Update state immediately for tracking
    const targetState = to > 0
      ? (mode === 'tilt' ? SectionState.TILT : SectionState.OPEN)
      : SectionState.CLOSED;
    this.state.config.sections[sectionIndex].openState = targetState;
  }

  update(dt: number, sectionInterfaces: Map<number, SectionInterface>): void {
    const completed: number[] = [];

    for (const anim of this.animations) {
      anim.elapsed += dt;
      const t = Math.min(anim.elapsed / anim.duration, 1);
      const eased = easeInOutCubic(t);
      const value = anim.from + (anim.to - anim.from) * eased;

      const section = sectionInterfaces.get(anim.sectionIndex + 1);
      const config = this.state.config.sections[anim.sectionIndex];
      if (section && config) {
        if (anim.mode === 'tilt' && section.setTiltAngle) {
          section.setTiltAngle(value * getTiltAngle());
        } else if (section.setOpenAngle) {
          section.setOpenAngle(value * getMaxAngle(config.type));
        }
        config.openAmount = value;
      }

      if (t >= 1) {
        completed.push(anim.sectionIndex);
      }
    }

    this.animations = this.animations.filter(a => !completed.includes(a.sectionIndex));
  }

  get isAnimating(): boolean {
    return this.animations.length > 0;
  }
}
